"use client";

import { useState } from "react";
const COOKIE_CONSENT_KEY = "amor-cookie-consent";
const COOKIE_CONSENT_VERSION = "1.0";
export interface CookieConsentPreferences {
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  version: string;
  timestamp: number;
}

/**
 * Hook personnalisé pour gérer le consentement des cookies
 * Conforme RGPD avec stockage localStorage
 */
export function useCookieConsent() {
  const initialPreferences = (() => {
    try {
      if (typeof window === "undefined") return null;
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (!stored) return null;
      const parsed = JSON.parse(stored) as CookieConsentPreferences;

      // Validation des données
      if (typeof parsed !== "object" || !parsed.version) {
        localStorage.removeItem(COOKIE_CONSENT_KEY);
        return null;
      }
      return parsed;
    } catch (error) {
      // Ne logger qu'en développement
      if (process.env.NODE_ENV === "development") {
        console.error("Erreur lors du chargement des préférences cookies:", error);
      }
      localStorage.removeItem(COOKIE_CONSENT_KEY);
      return null;
    }
  })();
  const [preferences, setPreferences] = useState<CookieConsentPreferences | null>(initialPreferences);
  const [isLoading] = useState(false);
  const [hasInteracted, setHasInteracted] = useState<boolean>(initialPreferences !== null);

  // Sauvegarder les préférences
  const savePreferences = (prefs: Omit<CookieConsentPreferences, "version" | "timestamp">) => {
    const fullPrefs: CookieConsentPreferences = {
      ...prefs,
      version: COOKIE_CONSENT_VERSION,
      timestamp: Date.now()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(fullPrefs));
    setPreferences(fullPrefs);
    setHasInteracted(true);

    // Émettre un événement pour notifier les changements
    window.dispatchEvent(new CustomEvent("cookieConsentChange", {
      detail: fullPrefs
    }));
  };

  // Accepter tous les cookies
  const acceptAll = () => {
    savePreferences({
      functional: true,
      analytics: true,
      marketing: true
    });
  };

  // Refuser les cookies non-essentiels
  const rejectAll = () => {
    savePreferences({
      functional: true,
      analytics: false,
      marketing: false
    });
  };

  // Sauvegarder des préférences personnalisées
  const saveConsent = (prefs_0: {
    functional: boolean;
    analytics: boolean;
    marketing: boolean;
  }) => {
    savePreferences(prefs_0);
  };

  // Vérifier si un type de cookie est autorisé
  const hasConsent = (type: "functional" | "analytics" | "marketing"): boolean => {
    if (!preferences) return false;
    return preferences[type] === true;
  };

  // Réinitialiser les préférences
  const resetConsent = () => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    setPreferences(null);
    setHasInteracted(false);
  };
  return {
    preferences,
    isLoading,
    hasInteracted,
    acceptAll,
    rejectAll,
    saveConsent,
    hasConsent,
    resetConsent
  };
}